/**
 * Politica de privacidad.
 *
 * La pide la Ley 21.719 de proteccion de datos personales (vigente desde el
 * 01-12-2026): quien trata datos tiene que mantener publicada, y siempre a la
 * mano, una politica que diga quien es, que datos trata, para que, con quien
 * los comparte, cuanto los guarda y como se ejercen los derechos.
 *
 * Escrita sobre lo que el sitio hace de verdad —que pide el checkout, que
 * guarda la base, que proveedores reciben datos (Random/PRIA, Transbank,
 * Resend, AWS, Google Maps y Google Analytics; las tipografias se sirven desde
 * el propio sitio desde el 03-10-2026), que la foto de
 * la busqueda no se guarda y que la unica medicion es Google Analytics, con
 * sus cookies y sin aviso de consentimiento (decision del cliente,
 * 02-10-2026; `medicion/analytics.ts`). Si algo de eso cambia —otra
 * medicion, un proveedor nuevo—, este texto cambia en el mismo turno.
 */
import { DocumentoLegal } from '../componentes/legal/DocumentoLegal';
import { SITIO } from '../configuracion/sitio';
import { rutas } from '../utilidades/rutas';

export function Privacidad() {
  const correo = <a href={`mailto:${SITIO.email}`}>{SITIO.email}</a>;

  return (
    <DocumentoLegal
      titulo="Política de privacidad"
      descripcion={`Qué datos personales trata ${SITIO.nombre}, para qué, con quién los comparte y cómo ejercer tus derechos.`}
      ruta={rutas.privacidad()}
      actualizado="3 de octubre de 2026"
    >
      <section>
        <h2>1. Quién es responsable de tus datos</h2>
        <p>
          {SITIO.razonSocial}, RUT {SITIO.rut}, con domicilio en {SITIO.direccion.calle},{' '}
          {SITIO.direccion.comuna}, dueña de {SITIO.nombreLargo}. Para cualquier tema relacionado
          con tus datos personales puedes escribirnos a {correo}.
        </p>
      </section>

      <section>
        <h2>2. Qué datos tratamos y para qué</h2>

        <h3>Cuando compras</h3>
        <ul>
          <li>
            <strong>Nombre, correo y teléfono</strong>: para procesar tu pedido, enviarte la
            confirmación y el documento tributario, y contactarte si hay algún problema con la
            compra o la entrega.
          </li>
          <li>
            <strong>Dirección de despacho</strong>, solo si eliges despacho: para entregarte el
            pedido.
          </li>
          <li>
            <strong>RUT</strong> con boleta, y con factura el{' '}
            <strong>RUT, razón social, giro y dirección de facturación</strong> de la empresa: para
            emitir el documento tributario.
          </li>
          <li>
            <strong>Datos del pago</strong>: el pago lo procesa Transbank en Webpay y nosotros
            nunca vemos el número de tu tarjeta. Guardamos lo que Webpay nos informa para respaldar
            la compra: código de autorización, tipo de pago, cuotas y los últimos cuatro dígitos de
            la tarjeta.
          </li>
        </ul>
        <p>
          Tratamos estos datos porque son necesarios para cumplir la compra que nos pides y para
          cumplir nuestras obligaciones tributarias.
        </p>

        <h3>Ofertas por correo</h3>
        <p>
          Solo si marcas la casilla «Quiero recibir ofertas y novedades por correo» al comprar. Es
          voluntario, no condiciona la compra y puedes retirar tu consentimiento cuando quieras
          escribiéndonos a {correo}.
        </p>

        <h3>Búsqueda por foto</h3>
        <p>
          Cuando buscas un producto con una foto, la procesamos en nuestro servidor solo para
          encontrar productos parecidos y la descartamos en el momento: no se guarda ni se comparte
          con nadie.
        </p>

        <h3>Uso del sitio</h3>
        <p>
          Registramos, sin datos que te identifiquen, los términos que se buscan en la tienda y los
          clics en los botones de WhatsApp, para mejorar el catálogo. Usamos además Google
          Analytics para saber cómo se usa la tienda (qué páginas se visitan, desde dónde se llega,
          desde qué tipo de dispositivo, los clics en WhatsApp, qué productos se ven y se agregan
          al carro, cuándo se llega al checkout y al pago y, de cada compra, el número de
          pedido, el monto y los productos), con estadísticas que no nos dicen quién eres: no le
          enviamos tu nombre, correo, teléfono, dirección ni RUT. Nuestros
          servidores guardan
          además registros técnicos de cada visita (dirección IP, navegador, página pedida) por
          seguridad y para resolver fallas.
        </p>

        <h3>WhatsApp</h3>
        <p>
          Si nos escribes por WhatsApp, esa conversación ocurre en WhatsApp, un servicio de Meta,
          y se rige también por sus condiciones. Usamos lo que nos cuentes ahí solo para atender tu
          consulta o tu pedido.
        </p>
      </section>

      <section>
        <h2>3. Con quién compartimos tus datos</h2>
        <p>
          No vendemos ni arrendamos tus datos. Los compartimos solo con quienes nos ayudan a
          cumplir tu compra, y solo lo necesario:
        </p>
        <ul>
          <li>
            <strong>Nuestro sistema de gestión (Random, de PRIA)</strong>: recibe tu pedido para
            registrarlo, prepararlo y emitir la boleta o factura.
          </li>
          <li>
            <strong>Transbank</strong>: procesa el pago en Webpay.
          </li>
          <li>
            <strong>Resend</strong>: envía los correos de confirmación y del documento tributario,
            y el aviso de cada compra a nuestro equipo.
          </li>
          <li>
            <strong>Amazon Web Services</strong>: aloja los servidores y la base de datos del sitio.
          </li>
          <li>
            <strong>Google</strong>: el mapa de la sala de ventas del pie de página se carga desde
            Google Maps. Al cargarlo, tu navegador le entrega a Google tu dirección IP. Google
            Analytics recibe además cómo navegas la tienda, como se explica en la sección 5.
          </li>
          <li>
            <strong>El Servicio de Impuestos Internos</strong> y otras autoridades, cuando la ley lo
            exige.
          </li>
        </ul>
        <p>
          Amazon Web Services, Resend y Google guardan los datos en servidores ubicados en Estados
          Unidos.
        </p>
      </section>

      <section>
        <h2>4. Cuánto tiempo guardamos tus datos</h2>
        <p>
          Los datos de tus compras los guardamos mientras sean necesarios para la garantía, los
          reclamos y nuestras obligaciones tributarias. El consentimiento para ofertas dura hasta
          que lo retires.
        </p>
      </section>

      <section>
        <h2>5. Lo que se guarda en tu navegador</h2>
        <p>
          El sitio usa el almacenamiento de tu navegador para que la tienda funcione: tu carro, los datos que escribes al comprar (para que no se pierdan si
          el pago falla; se borran cuando el pago se aprueba) y si prefieres el tema claro u
          oscuro. Nada de eso sale de tu navegador hasta que compras, y puedes borrarlo desde la
          configuración del navegador.
        </p>
        <p>
          Además, Google Analytics guarda cookies de medición (las que empiezan con «_ga») por hasta
          dos años, para distinguir una visita nueva de una que vuelve. No las usamos para mostrarte
          publicidad. Puedes bloquearlas desde la configuración de tu navegador o con el
          complemento de inhabilitación de Google Analytics, sin que la tienda deje de funcionar.
        </p>
        <p>El mapa de Google Maps puede usar sus propias cookies, según las reglas de Google.</p>
      </section>

      <section>
        <h2>6. Tus derechos</h2>
        <p>Sobre tus datos personales tienes derecho a:</p>
        <ul>
          <li>
            <strong>Acceso</strong>: saber qué datos tuyos tenemos y qué hacemos con ellos.
          </li>
          <li>
            <strong>Rectificación</strong>: corregirlos si están mal o incompletos.
          </li>
          <li>
            <strong>Supresión</strong>: pedir que los borremos, salvo los que la ley nos obliga a
            conservar.
          </li>
          <li>
            <strong>Oposición</strong>: pedir que dejemos de usarlos para un fin, como las ofertas.
          </li>
          <li>
            <strong>Portabilidad</strong>: recibirlos en un formato que puedas llevar a otro lado.
          </li>
          <li>
            <strong>Bloqueo</strong>: pedir que suspendamos su uso mientras se resuelve una
            solicitud tuya.
          </li>
        </ul>
        <p>
          Para ejercerlos, escríbenos a {correo} desde el correo con que compraste, o indicando tu
          RUT, y dinos qué necesitas. Si no quedas conforme con nuestra respuesta, puedes reclamar
          ante la Agencia de Protección de Datos Personales.
        </p>
      </section>

      <section>
        <h2>7. Cómo cuidamos tus datos</h2>
        <p>
          El sitio funciona siempre con conexión cifrada (HTTPS). Al panel de administración solo
          entran personas autorizadas de El Carpintero, con clave personal, y las claves se guardan
          cifradas.
        </p>
      </section>

      <section>
        <h2>8. Menores de edad</h2>
        <p>La tienda está dirigida a personas mayores de edad.</p>
      </section>

      <section>
        <h2>9. Cambios a esta política</h2>
        <p>
          Si cambiamos esta política, publicaremos la nueva versión en esta misma página con su
          fecha.
        </p>
      </section>
    </DocumentoLegal>
  );
}
